/**
 * Typed moment adapter — this module supersedes `app/utils/datetime/helpers.js`,
 * which is the migration target for the moment → adapter work and must not grow
 * new helpers. New date utilities belong here; existing callers of the legacy
 * surface migrate incrementally through this module.
 */
import moment from 'moment'

import momentTimezoneVendored from 'app/constants/moment_timezone'

type MomentInput = moment.MomentInput
type MomentFormat = moment.MomentFormatSpecification
type Moment = moment.Moment
type Base = moment.unitOfTime.Base
type StartOf = moment.unitOfTime.StartOf
type Diff = moment.unitOfTime.Diff
type DurationConstructor = moment.unitOfTime.DurationConstructor
type DurationInputArg1 = moment.DurationInputArg1
type CalendarSpec = moment.CalendarSpec
type Inclusivity = '()' | '[)' | '(]' | '[]'

type MomentWithTz = typeof moment & {
  tz: (value: MomentInput | Moment, timezone: string) => Moment
}

const momentTz = momentTimezoneVendored as unknown as MomentWithTz

const hasValue = (value: unknown): boolean =>
  value !== undefined && value !== null

const asMoment = (
  value?: MomentInput | Moment,
  inputFormat?: MomentFormat,
  strict?: boolean,
): Moment => {
  if (moment.isMoment(value)) {
    return value
  }

  if (hasValue(inputFormat)) {
    return moment(value, inputFormat, strict)
  }

  if (hasValue(value)) {
    return moment(value)
  }

  return moment()
}

export const now = (): Moment => moment()

export const parse = (value?: MomentInput): Moment => asMoment(value)

export const parseFormat = (
  value: MomentInput,
  inputFormat: MomentFormat,
  strict?: boolean,
): Moment => asMoment(value, inputFormat, strict)

export const format = (value: MomentInput, outputFormat?: string): string =>
  asMoment(value).format(outputFormat)

export const utc = (
  value?: MomentInput,
  inputFormat?: MomentFormat,
  strict?: boolean,
): Moment => {
  if (hasValue(inputFormat)) {
    return moment.utc(value, inputFormat, strict)
  }

  if (hasValue(value)) {
    return moment.utc(value)
  }

  return moment.utc()
}

export const local = (value?: MomentInput): Moment => asMoment(value).local()

export const tz = (value: MomentInput, timezone: string): Moment =>
  momentTz.tz(asMoment(value), timezone)

export const add = (
  value: MomentInput,
  amount: DurationInputArg1,
  unit?: DurationConstructor,
): Moment => asMoment(value).add(amount, unit)

export const subtract = (
  value: MomentInput,
  amount: DurationInputArg1,
  unit?: DurationConstructor,
): Moment => asMoment(value).subtract(amount, unit)

export const diff = (
  left: MomentInput,
  right: MomentInput,
  unit?: Diff,
  precise?: boolean,
): number => asMoment(left).diff(right, unit, precise)

export const startOf = (value: MomentInput, unit: StartOf): Moment =>
  asMoment(value).startOf(unit)

export const endOf = (value: MomentInput, unit: StartOf): Moment =>
  asMoment(value).endOf(unit)

export const isBefore = (
  value: MomentInput,
  compareTo: MomentInput,
  unit?: StartOf,
): boolean => asMoment(value).isBefore(compareTo, unit)

export const isAfter = (
  value: MomentInput,
  compareTo: MomentInput,
  unit?: StartOf,
): boolean => asMoment(value).isAfter(compareTo, unit)

export const isSame = (
  value: MomentInput,
  compareTo: MomentInput,
  unit?: StartOf,
): boolean => asMoment(value).isSame(compareTo, unit)

export const isBetween = (
  value: MomentInput,
  lower: MomentInput,
  upper: MomentInput,
  unit?: StartOf,
  inclusivity?: Inclusivity,
): boolean => asMoment(value).isBetween(lower, upper, unit, inclusivity)

export const isSameOrBefore = (
  value: MomentInput,
  compareTo: MomentInput,
  unit?: StartOf,
): boolean => asMoment(value).isSameOrBefore(compareTo, unit)

export const isSameOrAfter = (
  value: MomentInput,
  compareTo: MomentInput,
  unit?: StartOf,
): boolean => asMoment(value).isSameOrAfter(compareTo, unit)

export const isValid = (
  value?: MomentInput,
  inputFormat?: MomentFormat,
  strict?: boolean,
): boolean => asMoment(value, inputFormat, strict).isValid()

export const clone = (value: MomentInput): Moment => asMoment(value).clone()

export const valueOf = (value: MomentInput): number => asMoment(value).valueOf()

export const toDate = (value: MomentInput): Date => asMoment(value).toDate()

export const set = (value: MomentInput, unit: Base, amount: number): Moment =>
  asMoment(value).set(unit, amount)

export function day(value: MomentInput): number
export function day(value: MomentInput, input: number | string): Moment
export function day(
  value: MomentInput,
  input?: number | string,
): number | Moment {
  return hasValue(input)
    ? asMoment(value).day(input as number | string)
    : asMoment(value).day()
}

export function isoWeekday(value: MomentInput): number
export function isoWeekday(value: MomentInput, input: number | string): Moment
export function isoWeekday(
  value: MomentInput,
  input?: number | string,
): number | Moment {
  return hasValue(input)
    ? asMoment(value).isoWeekday(input as number | string)
    : asMoment(value).isoWeekday()
}

export function year(value: MomentInput): number
export function year(value: MomentInput, input: number): Moment
export function year(value: MomentInput, input?: number): number | Moment {
  return hasValue(input)
    ? asMoment(value).year(input as number)
    : asMoment(value).year()
}

export function month(value: MomentInput): number
export function month(value: MomentInput, input: number | string): Moment
export function month(
  value: MomentInput,
  input?: number | string,
): number | Moment {
  return hasValue(input)
    ? asMoment(value).month(input as number | string)
    : asMoment(value).month()
}

export function date(value: MomentInput): number
export function date(value: MomentInput, input: number): Moment
export function date(value: MomentInput, input?: number): number | Moment {
  return hasValue(input)
    ? asMoment(value).date(input as number)
    : asMoment(value).date()
}

export function hour(value: MomentInput): number
export function hour(value: MomentInput, input: number): Moment
export function hour(value: MomentInput, input?: number): number | Moment {
  return hasValue(input)
    ? asMoment(value).hour(input as number)
    : asMoment(value).hour()
}

export function minute(value: MomentInput): number
export function minute(value: MomentInput, input: number): Moment
export function minute(value: MomentInput, input?: number): number | Moment {
  return hasValue(input)
    ? asMoment(value).minute(input as number)
    : asMoment(value).minute()
}

export function second(value: MomentInput): number
export function second(value: MomentInput, input: number): Moment
export function second(value: MomentInput, input?: number): number | Moment {
  return hasValue(input)
    ? asMoment(value).second(input as number)
    : asMoment(value).second()
}

export const calendar = (
  value: MomentInput,
  referenceTime?: MomentInput,
  formats?: CalendarSpec,
): string => asMoment(value).calendar(referenceTime, formats)

export const duration = (
  value: DurationInputArg1,
  unit?: DurationConstructor,
): moment.Duration =>
  hasValue(unit) ? moment.duration(value, unit) : moment.duration(value)

export const fromNow = (value: MomentInput, withoutSuffix?: boolean): string =>
  asMoment(value).fromNow(withoutSuffix)

export const humanizeDuration = (
  value: DurationInputArg1,
  withSuffix?: boolean,
): string => duration(value).humanize(withSuffix)

/**
 * Escape hatch to reach raw moment / moment-timezone for the migration window
 * only. Intentionally NOT re-exported from `app/lib/date` so the typed barrel
 * remains the single public import surface; callers that genuinely need raw
 * moment during the migration must import from `app/lib/date/adapter/moment`
 * explicitly, and that usage must be deleted before the migration closes.
 */
export const raw = {
  asMoment,
  moment,
  momentTz,
}
